import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  ColleagueDto,
  CreateStoreroomWorkerInput,
  StoreroomWorkerDto,
  UpdateStoreroomWorkerInput,
  WorkerPinResultDto,
} from '@eombor/contracts';
import type { ApiClientError } from '../../../shared/api/client.js';
import { storekeepingApi, type UsagePage, type UsageQuery } from '../api/storekeeping.api.js';

/* Hammasi `['subdivision', 'mine', …]` ostida: ombor, qoldiq va tarix bitta
   kalit bilan yangilanadi (ishlatish qoldiqni ham o'zgartiradi). */
export const storekeepingKeys = {
  workers: ['subdivision', 'mine', 'workers'] as const,
  colleagues: (search: string) => ['subdivision', 'mine', 'colleagues', search] as const,
  usages: (query: UsageQuery) =>
    ['subdivision', 'mine', 'usages', query.storeroomId ?? 'all', query.workerId ?? 'all', query.page ?? 1] as const,
};

export const useWorkers = (enabled = true): UseQueryResult<StoreroomWorkerDto[], ApiClientError> =>
  useQuery<StoreroomWorkerDto[], ApiClientError>({
    queryKey: storekeepingKeys.workers,
    queryFn: () => storekeepingApi.workers(),
    enabled,
    retry: false,
  });

export const useColleagues = (search: string, enabled: boolean): UseQueryResult<ColleagueDto[], ApiClientError> =>
  useQuery<ColleagueDto[], ApiClientError>({
    queryKey: storekeepingKeys.colleagues(search),
    queryFn: () => storekeepingApi.colleagues(search),
    enabled,
    retry: false,
    placeholderData: (previous) => previous,
  });

export const useUsages = (query: UsageQuery): UseQueryResult<UsagePage, ApiClientError> =>
  useQuery<UsagePage, ApiClientError>({
    queryKey: storekeepingKeys.usages(query),
    queryFn: () => storekeepingApi.usages(query),
    retry: false,
    placeholderData: (previous) => previous,
  });

const useMineMutation = <TInput, TResult>(
  run: (input: TInput) => Promise<TResult>,
): UseMutationResult<TResult, ApiClientError, TInput> => {
  const queryClient = useQueryClient();
  return useMutation<TResult, ApiClientError, TInput>({
    mutationFn: run,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['subdivision', 'mine'] }),
  });
};

export const useCreateWorker = () =>
  useMineMutation<CreateStoreroomWorkerInput, WorkerPinResultDto>((input) => storekeepingApi.createWorker(input));

export const useUpdateWorker = () =>
  useMineMutation<{ id: string; input: UpdateStoreroomWorkerInput }, StoreroomWorkerDto>(({ id, input }) =>
    storekeepingApi.updateWorker(id, input),
  );

export const useDeleteWorker = () =>
  useMineMutation<string, void>((id) => storekeepingApi.deleteWorker(id));

export const useResetPin = () => useMineMutation<string, WorkerPinResultDto>((id) => storekeepingApi.resetPin(id));
